import {FlowStatePresentation} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation";
import {FlowStateController} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import {TimelineLite} from "gsap";
import {DjPsycho} from "../DjPsycho";
import {DjPsychoReelsView} from "../reels/DjPsychoReelsView";
import {ISimpleReelModelState, SimpleReelModel} from "../../core/reel/simplereel/SimpleReelModel";
import {DjPsychoGameMode} from "../server/DjPsychoGameMode";
import {APIFeatureBetEventData} from "@nolimitcity/slot-launcher/bin/interfaces/APIBetEventData";
import {APIEvent} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {APIBetType} from "@nolimitcity/slot-launcher/bin/interfaces/APIBetType";
import {DjPsychoSound} from "../sound/DjPsychoSound";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {DjPsychoSymbol} from "../reels/DjPsychoSymbol";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {DjPsychoIdleNormalPresentation} from "./DjPsychoIdleNormalPresentation";

interface IAPIFeatureBet extends APIFeatureBetEventData {
    replayAndFeatureBuy: boolean;
}

export class DjPsychoNormalBetPresentation implements FlowStatePresentation {
    private _flowStateController: FlowStateController;
    private _startCalled: boolean;
    private _betCalled: boolean;
    private _isExternalFreeBetActive: boolean; // #184

    constructor() {
        this.updateExternalFreeBetActive(false);

        DjPsycho.apiPlugin.events.on(APIEvent.BET, (betData: IAPIFeatureBet) => this.onBet(betData));
        DjPsycho.apiPlugin.events.on('extFreeRound', () => this.updateExternalFreeBetActive(true));
        DjPsycho.apiPlugin.events.on('extFreeRoundsEnded', () => this.updateExternalFreeBetActive(false));
    }

    public start(flowStateController: FlowStateController): void {
        this._flowStateController = flowStateController;

        // #148
        DjPsychoIdleNormalPresentation.killTl();

        // #78
        this._startCalled = true;
        this.onCompletePresentation();
    }

    // #184
    private updateExternalFreeBetActive(value: boolean): void {
        this._isExternalFreeBetActive = value;
    }

    private onBet(betData: IAPIFeatureBet): TimelineLite {
        const tl: TimelineLite = new TimelineLite({
                onComplete: () => {
                    // #78
                    this._betCalled = true;

                    this.onCompletePresentation();
                }
            }),
            delay: number = DjPsychoReelsView.stepDuration * 2;

        // #148
        DjPsychoIdleNormalPresentation.killTl();

        DjPsycho.miniCollectors.resetIndexes();
        DjPsycho.reelView.reelModels.forEach((reel: SimpleReelModel) => {
            reel.symbols.forEach((sym: DjPsychoSymbol) => {
                sym.enable(false);
            });
        });

        // #167
        DjPsycho.anyBonusBought = false;

        // #95
        DjPsycho.sfsBought = false;

        if (DjPsycho.model.data.nextMode.indexOf("AVALANCHE") > -1) {
            // during restore, "start" method is called after "onBet", hence adding some time here.
            if (!this._flowStateController) {
                tl.to(this, {
                    duration: 0.1
                });
            }
            return tl;
        }

        tl.add(() => {
            if (DjPsycho.model.data.nextMode === DjPsychoGameMode.NORMAL) {
                DjPsycho.totalMultiplier.turnOff();
                DjPsycho.miniCollectors.deActivateFSTexts();
                DjPsycho.miniCollectors.deActivateSFSTexts();
                DjPsycho.miniCollectors.deActivateColorIndicators();
                DjPsycho.miniCollectors.toggleFSDisabled(true);
            } else if (DjPsycho.model.data.nextMode === DjPsychoGameMode.FREESPIN) {
                DjPsycho.totalMultiplier.turnOff();
            }
        }, 0);

        // #184 -> ugly solution as "betData.replayAndFeatureBuy" is handled in a ugly way in case of externalFreeBets(EVO), check "FreeBetsHandler" class method "injectExternalFreeBetCallback"
        if (this._isExternalFreeBetActive && betData.type === APIBetType.NORMAL_BET && betData.replayAndFeatureBuy) {
            betData.replayAndFeatureBuy = false;
        }

        if ((betData.type !== APIBetType.FEATURE_BET && !betData.replayAndFeatureBuy) || (betData.playerInteraction && betData.playerInteraction.featureName === "BOOSTED_BET")) {
            tl.add(() => {
                // #163
                DjPsycho.soundPlayer.killReelsSpinSound();

                DjPsycho.soundPlayer.playSound(DjPsychoSound.REELS_SPINNING, 1, Number.MAX_VALUE);

                DjPsycho.reelView.reelModels.forEach((reel: SimpleReelModel) => {
                    reel.state = ISimpleReelModelState.DISAPPEAR;
                });
            }, 0);

            const randomIndex: number = MathHelper.randomNumberInRange(0, 2),
                isFastSpin: boolean = DjPsycho.apiPlugin.settings.get(APISetting.FAST_SPIN, false);
            for (let idx: number = 0; idx < DjPsycho.rowsNum; idx++) {
                DjPsycho.reelView.reelModels.forEach((reel: SimpleReelModel) => {
                    tl.add(reel.disappearSymbolAt(idx, randomIndex), (isFastSpin ? 0 : delay * idx));
                });
            }
        } else if ((betData.type === APIBetType.FEATURE_BET || betData.replayAndFeatureBuy)) {
            // #52
            const isSFS: boolean = (betData.featureName === DjPsycho.SFS_BUY_FEATURE_NAME || DjPsycho.buyFeatureName === DjPsycho.SFS_BUY_FEATURE_NAME);

            // #167
            DjPsycho.anyBonusBought = true;

            // #95
            DjPsycho.sfsBought = isSFS;

            if (!isSFS) {
                tl.add(DjPsycho.bonusBuy.play());
            }
        }

        return tl;
    }

    private onCompletePresentation(): void {
        // #78
        if (this._startCalled && this._betCalled) {
            this._flowStateController.finish();
            this._startCalled = this._betCalled = false;
        }
    }
}